import { useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import {
  legacyScheduleBatchColorOptions,
  legacyScheduleBatchOfficeOptions,
  legacyScheduleBatchWorkerOptions,
  type LegacyScheduleBatchColor,
} from '../../mock/legacyScheduleBatch'
import './LegacyScheduleBatch.css'

type WeekKey = 'all_weeks' | 'first_weeks' | 'second_weeks' | 'third_weeks' | 'forth_weeks' | 'last_weeks'
type WorkerMode = 'all' | 'none' | 'choice'
type Preview = { date: string; weekday: string }
type ModalState = { kind: 'alert' | 'confirm' | 'complete'; message: string } | null

const weekGroups: ReadonlyArray<{ key: WeekKey; label: string; weekNumber: number }> = [
  { key: 'all_weeks', label: '매주', weekNumber: 0 },
  { key: 'first_weeks', label: '첫째주', weekNumber: 1 },
  { key: 'second_weeks', label: '둘째주', weekNumber: 2 },
  { key: 'third_weeks', label: '셋째주', weekNumber: 3 },
  { key: 'forth_weeks', label: '넷째주', weekNumber: 4 },
  // 원본은 달력상의 마지막 주가 아니라 weekNumber === 5로 계산한다.
  { key: 'last_weeks', label: '마지막주', weekNumber: 5 },
]

const dayNames = [
  { short: '일', long: '일요일' },
  { short: '월', long: '월요일' },
  { short: '화', long: '화요일' },
  { short: '수', long: '수요일' },
  { short: '목', long: '목요일' },
  { short: '금', long: '금요일' },
  { short: '토', long: '토요일' },
]

const checkerboardStyle: CSSProperties = {
  backgroundColor: 'transparent',
  backgroundImage: 'repeating-linear-gradient(45deg, #eaeaea 25%, transparent 25%, transparent 75%, #eaeaea 75%, #eaeaea), repeating-linear-gradient(45deg, #eaeaea 25%, #fff 25%, #fff 75%, #eaeaea 75%, #eaeaea)',
  backgroundPosition: '0 0, 8px 8px',
  backgroundSize: '16px 16px',
}

const legacyScheduleBatchDefaultColor: LegacyScheduleBatchColor = {
  id: 'mock-color-default',
  name: '기본색',
  value: '#131814',
}

function parseLocalDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const date = new Date(year, month - 1, day, 12)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return date
}

function formatLocalDate(date: Date) {
  const year = String(date.getFullYear()).padStart(4, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function timeToMinutes(value: string) {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

export function LegacyScheduleBatch() {
  const initialOffice = useMemo(() => {
    const requested = new URLSearchParams(window.location.search).get('office_id_id')
    return legacyScheduleBatchOfficeOptions.find((item) => item.id === requested) ?? legacyScheduleBatchOfficeOptions[0]
  }, [])
  const [officeId, setOfficeId] = useState(initialOffice.id)
  const [officeOpen, setOfficeOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [color, setColor] = useState<LegacyScheduleBatchColor | null>(null)
  const [colorOpen, setColorOpen] = useState(false)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [workerMode, setWorkerMode] = useState<WorkerMode>('all')
  const [workerId, setWorkerId] = useState('')
  const [weeks, setWeeks] = useState<Partial<Record<WeekKey, boolean[]>>>({})
  const [preview, setPreview] = useState<Preview[]>([])
  const [modal, setModal] = useState<ModalState>(null)

  useEffect(() => {
    document.title = '스케줄 일괄 등록'
  }, [])

  useEffect(() => {
    if (!colorOpen && !officeOpen) return
    const closeLists = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (!target?.closest('#color_selector')) setColorOpen(false)
      if (!target?.closest('#office_id_chosen')) setOfficeOpen(false)
    }
    document.addEventListener('pointerdown', closeLists)
    return () => document.removeEventListener('pointerdown', closeLists)
  }, [colorOpen, officeOpen])

  const alertMock = (message: string) => setModal({ kind: 'alert', message })
  const toggleAll = (key: WeekKey, value: boolean) => {
    clearWeekValidity()
    setWeeks((current) => ({ ...current, [key]: Array(7).fill(value) }))
  }
  const toggleDay = (key: WeekKey, index: number) => {
    clearWeekValidity()
    setWeeks((current) => ({
      ...current,
      [key]: Array.from({ length: 7 }, (_, dayIndex) => dayIndex === index ? !(current[key]?.[dayIndex] ?? false) : (current[key]?.[dayIndex] ?? false)),
    }))
  }
  const changeOffice = (value: string) => {
    setOfficeId(value)
    window.location.assign(`${window.location.pathname}?office_id_id=${encodeURIComponent(value)}`)
  }
  const changeWorkerMode = (value: WorkerMode) => {
    setWorkerMode(value)
    if (value !== 'choice') setWorkerId('')
  }

  const makePreview = () => {
    for (const id of ['schedule_title', 'start_date', 'end_date', 'photo_start', 'photo_end']) {
      const field = document.getElementById(id) as HTMLInputElement | null
      if (field && !field.checkValidity()) {
        field.reportValidity()
        return
      }
    }
    const from = parseLocalDate(start)
    const to = parseLocalDate(end)
    if (!from || !to) {
      alertMock('시작 및 종료일을 YYYY-MM-DD 형식으로 입력하세요.')
      return
    }
    if (from > to) {
      alertMock('종료일은 시작일 이후로 입력하세요.')
      return
    }

    const startMinutes = timeToMinutes(startTime)
    const endMinutes = timeToMinutes(endTime)
    if (startMinutes === null || endMinutes === null) {
      alertMock('시작 및 종료 시간을 HH:MM 형식으로 입력하세요.')
      return
    }
    if (startMinutes < 9 * 60 || startMinutes > 19 * 60 + 59 || endMinutes < 9 * 60 + 9 || endMinutes > 19 * 60 + 59) {
      alertMock('시간은 원본 입력 범위(시작 09:00~19:59, 종료 09:09~19:59) 안에서 선택하세요.')
      return
    }
    if (startMinutes >= endMinutes) {
      alertMock('종료 시간은 시작 시간 이후로 입력하세요.')
      return
    }
    if (!weekGroups.some((group) => weeks[group.key]?.some(Boolean))) {
      const firstWeekday = document.querySelector('.legacy-s04 .g_checked_toggle') as HTMLInputElement | null
      firstWeekday?.setCustomValidity('요일을 선택하세요.')
      firstWeekday?.reportValidity()
      return
    }

    // 원본 getDates 호출 순서를 그대로 유지한다. 서로 다른 주차 그룹이 겹치면 같은 날짜도 중복된다.
    const dates: Preview[] = []
    weekGroups.forEach((group) => {
      weeks[group.key]?.forEach((selected, weekdayIndex) => {
        if (!selected) return
        const cursor = new Date(from)
        while (cursor <= to) {
          if (cursor.getDay() === weekdayIndex) {
            const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 12)
            const weekNumber = Math.ceil((cursor.getDate() - firstDay.getDate() + firstDay.getDay() + 1) / 7)
            if (group.weekNumber === 0 || weekNumber === group.weekNumber) {
              dates.push({ date: formatLocalDate(cursor), weekday: dayNames[cursor.getDay()].long })
            }
          }
          cursor.setDate(cursor.getDate() + 1)
        }
      })
    })
    dates.sort((left, right) => left.date.localeCompare(right.date))
    setPreview(dates)
    if (!dates.length) alertMock('선택한 일시가 유효하지 않습니다.')
  }

  const selectedWorker = legacyScheduleBatchWorkerOptions.find((item) => item.id === workerId)
  const selectedOffice = legacyScheduleBatchOfficeOptions.find((item) => item.id === officeId) ?? legacyScheduleBatchOfficeOptions[0]
  const workerName = workerMode === 'all' ? '모든 작업담당' : workerMode === 'choice' ? selectedWorker?.name ?? '' : ''
  const selectedColorStyle = color ? { backgroundColor: color.value, backgroundImage: 'none' } : checkerboardStyle

  return <div className="g_content_wrap g_content_full_size legacy-s04">
    <div className="l_shced_header">
      <h2 className="g_title">스케줄 일괄 등록</h2>
      <a className="g_btn l_link" href="/v1/sched_one_q_list/">스케줄 일괄 삭제</a>
    </div>
    <section className="g_card main_box">
      <div className="main_box_left min_w_400">
        <div className="box_info l_box_info">
          <div className="g_input_wrap sche_office flex_wrap gap_5"><label className="g_label title center office l_title" htmlFor="office_id">지점</label><select id="office_id" name="office_id" className="g_input chosen-select l_input width_2x" value={officeId} onChange={(event) => changeOffice(event.target.value)} tabIndex={-1} aria-hidden="true">{legacyScheduleBatchOfficeOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><div id="office_id_chosen" className={`chosen-container chosen-container-single${officeOpen ? ' chosen-with-drop chosen-container-active' : ''}`}><a className="chosen-single" href="#office_id_chosen" role="button" aria-haspopup="listbox" aria-expanded={officeOpen} onClick={(event) => { event.preventDefault(); setOfficeOpen((current) => !current) }}><span>{selectedOffice.name}</span><span className="s04-chosen-arrow" aria-hidden="true"><i /></span></a><div className="chosen-drop"><div className="chosen-search"><input type="text" aria-label="지점 검색" /></div><ul className="chosen-results" role="listbox">{legacyScheduleBatchOfficeOptions.map((item) => <li key={item.id} className={`active-result${item.id === officeId ? ' result-selected' : ''}`} role="option" aria-selected={item.id === officeId} onClick={() => changeOffice(item.id)}>{item.name}</li>)}</ul></div></div></div>
          <div className="sche_name flex_wrap gap_5"><Field required label="스케줄 제목"><input id="schedule_title" name="schedule_title" className="g_input width_2x" placeholder="제목" value={title} onChange={(event) => setTitle(event.target.value)} required /></Field><div className="g_input_wrap g_input checkbox_wrap"><input id="schedule_type" name="schedule_type" type="radio" value="일반일정" defaultChecked /><label htmlFor="schedule_type">일반일정</label></div></div>
          <div className="g_row"><div className="g_input_wrap l_align_item_center"><label className="g_label title center l_title">색상</label><span id="color_selector" className="color_selector l_js_e_schedule_color_list s04-colors"><input key={color?.id ?? 'unspecified'} type="hidden" id="selected_config_color" className="selected_config_color" defaultValue={color?.value ?? ''} /><span className="g_item selected_color_box" role="button" tabIndex={0} aria-label="색상 선택" aria-expanded={colorOpen} style={{ ...selectedColorStyle, position: 'relative' }} onClick={() => setColorOpen((current) => !current)} onKeyDown={(event) => activate(event, () => setColorOpen((current) => !current))}>{colorOpen && <span className="color_box g_scroll_bar" role="listbox" aria-label="Mock 색상 목록" onClick={(event) => event.stopPropagation()} style={{ position: 'absolute', zIndex: 9, top: 25, left: 0, display: 'block', minWidth: 130, maxHeight: 180, padding: 0, overflowY: 'auto', backgroundColor: '#000' }}><table className="l_js_e_table" style={{ width: '100%', borderCollapse: 'collapse' }}><tbody className="l_js_e_color_list"><ColorRow color={null} index="-" name="미지정" selected={!color} onSelect={() => { setColor(null); setColorOpen(false) }} /><ColorRow color={legacyScheduleBatchDefaultColor} index={0} name="기본색" selected={color?.id === legacyScheduleBatchDefaultColor.id} onSelect={() => { setColor(legacyScheduleBatchDefaultColor); setColorOpen(false) }} />{legacyScheduleBatchColorOptions.map((item, index) => <ColorRow key={item.id} color={item} index={index + 1} name={item.name} selected={color?.id === item.id} onSelect={() => { setColor(item); setColorOpen(false) }} />)}<tr style={{ backgroundColor: 'grey' }}><td colSpan={2} style={{ padding: 5, textAlign: 'center' }}><button className="g_btn g_btn_save size_2" type="button" onClick={() => { setColorOpen(false); window.open('/v1/color/', '_blank', 'noopener,noreferrer') }}>색상 설정</button></td></tr></tbody></table></span>}</span></span></div></div>
          <div className="g_row"><Field required label={<>시작 및<br /> 종료일</>}><div className="l_date_range_wrap"><input id="start_date" name="start_date" className="g_input g_input_datepicker l_date_picker" placeholder="YYYY-MM-DD" pattern="^\d{4}-\d{2}-\d{2}$" title="숫자와 - 만 입력 가능합니다." value={start} onChange={(event) => setStart(event.target.value)} required /><span>~</span><input id="end_date" name="end_date" className="g_input g_input_datepicker l_date_picker" placeholder="YYYY-MM-DD" pattern="^\d{4}-\d{2}-\d{2}$" title="숫자와 - 만 입력 가능합니다." value={end} onChange={(event) => setEnd(event.target.value)} required /></div></Field></div>
          <div className="sche_time flex_wrap gap_5"><div className="g_input_wrap required flex_wrap"><span className="g_label title center l_title">시작 및<br /> 종료 시간</span><div className="sche_date_box"><input name="photo_start" id="photo_start" className="g_input g_input_timepicker" value={startTime} onChange={(event) => setStartTime(event.target.value)} required /> ~ <input name="photo_end" id="photo_end" className="g_input g_input_timepicker" value={endTime} onChange={(event) => setEndTime(event.target.value)} required /></div><div className="g_text comment l_comment">* 시간을 지정하면 일 스케줄에서 표시됨</div></div></div>
          <div className="sche_worker flex_wrap gap_5"><div className="g_input_wrap type_check"><span className="g_label title center l_title">작업담당</span><label className="g_input radiobox_wrap"><input id="all_workgroup" className="g_input radio radiobox" name="workgroup" type="radio" checked={workerMode === 'all'} onChange={() => changeWorkerMode('all')} />모든 작업담당에 등록</label><label className="g_input radiobox_wrap"><input id="non_workgroup" className="g_input radio radiobox" name="workgroup" type="radio" checked={workerMode === 'none'} onChange={() => changeWorkerMode('none')} />작업담당 없음</label><label className="g_input radiobox_wrap"><input id="chocie_workgroup" className="g_input radio radiobox" name="workgroup" type="radio" checked={workerMode === 'choice'} onChange={() => changeWorkerMode('choice')} />선택</label><select name="manage_worker" id="manage_worker" className="g_input max_w_100" disabled={workerMode !== 'choice'} value={workerId} onChange={(event) => { const value = event.target.value; setWorkerId(value); setWorkerMode(value ? 'choice' : 'all') }}><option value="">-</option>{legacyScheduleBatchWorkerOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div></div>
          <div className="box_week"><div className="week_wrap"><ul>{weekGroups.map((group) => { const selectedDays = weeks[group.key]; const allSelected = selectedDays?.length === 7 && selectedDays.every(Boolean); return <li className="sche_week_box" key={group.key}><label className="g_input checkbox_wrap"><input className="g_input checkbox g_checked_toggle_all" type="checkbox" checked={allSelected} onChange={(event) => toggleAll(group.key, event.target.checked)} /><span>{group.label}</span></label><div className="week_day">{dayNames.map((day, index) => <label className="g_input checkbox_wrap sche_week_day" key={day.short}><input className="g_input checkbox g_checked_toggle" name={group.key} type="checkbox" value={index} checked={weeks[group.key]?.[index] ?? false} onChange={() => toggleDay(group.key, index)} />{day.short}</label>)}</div></li> })}</ul></div></div>
          <div className="box_left_prev margin_top_10"><button className="g_btn register" type="button" onClick={makePreview}>미리보기</button></div>
          <div className="box_left_desc" />
        </div>
        <div className="main_box_right"><div className="box_table g_list_wrap"><table className="g_list"><thead><tr><th>날짜</th><th>시간</th><th>제목</th><th>작업담당</th><th>등록</th></tr></thead><tbody id="l_tbody">{preview.map((row, index) => <tr key={`${row.date}-${row.weekday}-${index}`}><td>{row.date} <strong>{row.weekday}</strong></td><td>{startTime}~{endTime}</td><td>{title}</td><td>{workerName}</td><td>등록준비완료</td></tr>)}</tbody></table></div><div className="box_right_regist"><button className="g_btn save g_btn_submit" type="button" onClick={() => setModal({ kind: 'confirm', message: '스케줄을 등록 하시겠습니까?' })}>스케줄 일괄 등록하기</button></div></div>
      </div>
    </section>
    <CommonModal state={modal} onCancel={() => setModal(null)} onConfirm={() => {
      if (modal?.kind === 'confirm') setModal({ kind: 'complete', message: '스케줄이 등록되었습니다.' })
      else {
        if (modal?.kind === 'complete') setPreview([])
        setModal(null)
      }
    }} />
  </div>
}

function ColorRow({ color, index, name, selected, onSelect }: { color: LegacyScheduleBatchColor | null; index: number | string; name: string; selected: boolean; onSelect: () => void }) {
  const style: CSSProperties = color ? { backgroundColor: color.value, cursor: 'pointer' } : { ...checkerboardStyle, color: '#000', cursor: 'pointer', fontWeight: 800 }
  return <tr className={`text_color_select${color ? '' : ' l_remove_color'}`} role="option" aria-selected={selected} tabIndex={0} style={style} onClick={onSelect} onKeyDown={(event) => activate(event, onSelect)}><td className="color_count l_transparent_color_background" style={{ width: '30%', padding: 5, color: color ? '#fff' : '#000', textAlign: 'center' }}>{index}</td><td className="color_status l_transparent_color_background" style={{ width: '70%', padding: 5, color: color ? '#fff' : '#000' }}>{name}</td></tr>
}

function CommonModal({ state, onCancel, onConfirm }: { state: ModalState; onCancel: () => void; onConfirm: () => void }) {
  if (!state) return null
  return <div id="g_question_modal" className="g_question_modal s04-modal" role="dialog" aria-modal="true" aria-label={state.message}><div className="mini_modal"><div className="question">{state.message}</div><div className="flex_wrap gap_10 g_modal_button_wrap"><button id="g_yes_btn" className={`g_btn modalConfirm save${state.kind === 'confirm' ? ' s04-confirm-yes' : ''}`} type="button" onClick={onConfirm}>{state.kind === 'confirm' ? '예(Yes)' : 'Ok'}</button>{state.kind === 'confirm' && <button id="g_no_btn" className="g_btn modalConfirm close s04-confirm-no" type="button" onClick={onCancel}>아니오(No)</button>}</div></div></div>
}

function activate(event: KeyboardEvent, action: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  action()
}

function clearWeekValidity() {
  const firstWeekday = document.querySelector('.legacy-s04 .g_checked_toggle') as HTMLInputElement | null
  firstWeekday?.setCustomValidity('')
}

function Field({ label, children, required = false }: { label: ReactNode; children: ReactNode; required?: boolean }) {
  return <div className={`g_input_wrap${required ? ' required' : ''}`}><span className="g_label title center l_title">{label}</span>{children}</div>
}
